import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilmCard from "@/components/base/FilmCard";
import FilmCardSkeleton from "@/components/base/FilmCardSkeleton";
import EmptyState from "@/components/base/EmptyState";
import { hotFilms, searchFilms } from "@/lib/film";
import { useDelayedLoading } from "@/hooks/useDemoLoading";
import { usePageMeta } from "@/hooks/usePageMeta";

const quickKeywords = ["夜行列车", "悬疑", "京都", "爱情", "4K 修复", "治愈"];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const keyword = params.get("q") ?? "";
  const [value, setValue] = useState(keyword);

  useEffect(() => {
    setValue(keyword);
  }, [keyword]);

  useEffect(() => {
    if (value.trim() === keyword) return;
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(params);
      const trimmed = value.trim();
      if (trimmed) {
        next.set("q", trimmed);
      } else {
        next.delete("q");
      }
      setParams(next, { replace: true });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [value]);

  const results = searchFilms(keyword);
  const recommended = hotFilms(4);
  const loading = useDelayedLoading(keyword, 260);
  const showLoading = Boolean(keyword) && loading;

  usePageMeta({
    title: keyword ? `搜索「${keyword}」 - 日本一级片` : "搜索影片 - 日本一级片",
    description: keyword
      ? `日本一级片搜索「${keyword}」的影片结果，共 ${results.length} 部相关日本影片资料。`
      : "输入影片标题、导演或类型关键词，快速检索日本一级影片资料。",
    keywords: "日本影片搜索,日本电影检索,日本一级片",
  });

  const setQuickKeyword = (word: string) => {
    setValue(word);
    const next = new URLSearchParams(params);
    next.set("q", word);
    setParams(next, { replace: true });
  };

  return (
    <SiteLayout>
      <div className="bg-background-100 border-b border-background-200">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "搜索" }]} />
          <h1 className="mt-4 font-heading text-2xl md:text-3xl text-foreground-950">搜索影片</h1>
          <p className="mt-2 text-sm text-foreground-600">
            支持按影片标题、日文原名、导演、主演、类型与标签关键词检索。
          </p>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              const next = new URLSearchParams(params);
              const trimmed = value.trim();
              if (trimmed) {
                next.set("q", trimmed);
              } else {
                next.delete("q");
              }
              setParams(next);
            }}
            className="mt-5 flex flex-col sm:flex-row items-stretch gap-2 md:gap-3 max-w-[720px]"
          >
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base w-5 h-5 flex items-center justify-center"></i>
              <input
                value={value}
                onChange={(event) => setValue(event.target.value)}
                placeholder="输入影片标题，例如：夜行列车"
                aria-label="搜索影片关键词"
                className="w-full h-11 pl-11 pr-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-6 rounded-md bg-primary-600 text-background-50 text-sm whitespace-nowrap hover:bg-primary-700 transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex items-center flex-wrap gap-2">
            <span className="text-xs text-foreground-500">热门关键词</span>
            {quickKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => setQuickKeyword(word)}
                className="h-7 px-2.5 rounded-md bg-background-50 border border-background-300 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12">
        {keyword && (
          <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 mb-6">
            <p className="text-sm text-foreground-700">
              关键词「<span className="text-primary-700 font-medium">{keyword}</span>」共找到{" "}
              <span className="text-foreground-950 font-medium">{results.length}</span> 部影片
            </p>
            <Link
              to="/category"
              className="text-sm text-foreground-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              按分类浏览
            </Link>
          </div>
        )}

        {!keyword ? (
          <div>
            <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-4">热门影片推荐</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
              {recommended.map((film) => (
                <FilmCard key={film.id} film={film} />
              ))}
            </div>
          </div>
        ) : showLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {Array.from({ length: 4 }).map((_, index) => (
              <FilmCardSkeleton key={index} />
            ))}
          </div>
        ) : results.length === 0 ? (
          <div>
            <EmptyState
              title="未找到相关影片"
              description={`没有与「${keyword}」匹配的影片资料，试试更换关键词，或浏览热门分类。`}
              actionLabel="浏览全部分类"
              onAction={() => navigate("/category")}
              secondaryLabel="返回首页"
              secondaryPath="/"
            />
            <div className="mt-10">
              <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-4">你可能感兴趣</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
                {recommended.map((film) => (
                  <FilmCard key={film.id} film={film} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {results.map((film) => (
              <FilmCard key={film.id} film={film} />
            ))}
          </div>
        )}
      </div>
    </SiteLayout>
  );
}